import { useEffect, useState } from 'react';
import { Box, Checkbox, Flex, Text, TextInput } from '@mantine/core';
import type { ClientListItem } from 'src/admin/users/types';
import { isSelectableAdvisor, useAdvisors } from 'src/clients/hooks/useAdvisors';
import { useClients } from 'src/clients/hooks/useClients';
import {
  SelectPeopleModal,
  type SelectPeopleSection,
  type SelectPersonRow,
  type SelectResultRow,
} from 'src/design-system/SelectPeopleModal';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { matchesSearch } from 'src/utils/search';
import { useMeetingParticipants } from 'src/meetings/hooks/useMeetingParticipants';
import type { FamilyMeeting } from 'src/meetings/hooks/useScopedMeetings';
import type { MeetingScope } from 'src/meetings/scope';
import { isPastEvent } from 'src/meetings/utils/date-utils';
import { getAllClientParticipants } from 'src/meetings/utils/event-utils';
import { ExternalGuestLabel } from './AttendeeRow';
import { type Attendee, type AttendeeGroup, useMeetingAttendees } from './useMeetingAttendees';
import {
  type ProfessionalConnection,
  useProfessionalConnections,
} from './useProfessionalConnections';

type Props = {
  opened: boolean;
  onClose: () => void;
  scope: MeetingScope;
  meeting: FamilyMeeting;
};

type Section = {
  group: AttendeeGroup;
  title: string;
  addLabel: string;
  searchPlaceholder: string;
  noneLabel: string;
  addAsWord: string;
};

const SECTIONS: Section[] = [
  {
    group: 'adviser',
    title: 'Advisers',
    addLabel: 'Add adviser',
    searchPlaceholder: 'Search advisers',
    noneLabel: 'No advisers found',
    addAsWord: 'guest',
  },
  {
    group: 'guest',
    title: 'Guests',
    addLabel: 'Add guest',
    searchPlaceholder: 'Search clients',
    noneLabel: 'No such clients',
    addAsWord: 'guest',
  },
  {
    group: 'keyContact',
    title: 'Key contacts',
    addLabel: 'Add key contact',
    searchPlaceholder: 'Search professional connections',
    noneLabel: 'No such professional connections',
    addAsWord: 'key contact',
  },
];

const REQUIRED_GROUP_MESSAGES: Partial<Record<AttendeeGroup, string>> = {
  adviser: 'At least one adviser should be selected',
  guest: 'At least one guest should be selected',
};

function fullName(person: { firstName: string; lastName: string }): string {
  return `${person.firstName} ${person.lastName}`.trim();
}

function adviserToAttendee(advisor: { id: string; firstName: string; lastName: string }): Attendee {
  return {
    id: `user:${advisor.id}`,
    name: fullName(advisor),
    role: 'Adviser',
    group: 'adviser',
    participantId: null,
    defaultChecked: false,
    addBody: { participantType: 'USER', userId: advisor.id },
    isExternalGuest: false,
  };
}

function clientToAttendee(client: ClientListItem, group: AttendeeGroup): Attendee {
  return {
    id: `client:${client.id}`,
    name: fullName(client),
    role: 'Client',
    group,
    participantId: null,
    defaultChecked: false,
    addBody: { participantType: 'CLIENT', clientId: client.id },
    isExternalGuest: false,
  };
}

function connectionToAttendee(connection: ProfessionalConnection): Attendee {
  const role = connection.type || 'Key contact';
  return {
    id: `conn:${connection.id}`,
    name: connection.name,
    role,
    group: 'keyContact',
    participantId: null,
    defaultChecked: false,
    addBody: { participantType: 'EXTERNAL', name: connection.name, description: role },
    isExternalGuest: false,
  };
}

type CustomAttendee = { id: string; name: string; role: string; group: AttendeeGroup };

export function AddExpectedAttendeesModal({ opened, onClose, scope, meeting }: Props) {
  const meetingAttendees = useMeetingAttendees(scope, meeting);
  // A past meeting's attendees have already attended, so "expected" no longer fits.
  const modalTitle = isPastEvent(meeting) ? 'Add attendees' : 'Add expected attendees';
  // A client-less meeting (e.g. on the practice-wide hub) has no adviser/guest to
  // mandate, so we don't force the required groups — the user may just be jotting
  // down who attended.
  const meetingHasClient = getAllClientParticipants(meeting).length > 0;
  const { advisors } = useAdvisors();
  const { clients } = useClients();
  const connections = useProfessionalConnections(scope);
  const { addParticipant, removeParticipant } = useMeetingParticipants(scope);
  const [overrides, setOverrides] = useState<Record<string, boolean>>({});
  const [addedAdviserIds, setAddedAdviserIds] = useState<string[]>([]);
  const [addedClients, setAddedClients] = useState<{ clientId: string; group: AttendeeGroup }[]>(
    [],
  );
  const [addedConnectionIds, setAddedConnectionIds] = useState<string[]>([]);
  const [customAttendees, setCustomAttendees] = useState<CustomAttendee[]>([]);
  const [customSeq, setCustomSeq] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [validationTriggered, setValidationTriggered] = useState(false);

  // The modal stays mounted at some call sites, so clear all local selection
  // state once it closes to avoid stale checkboxes and re-adding saved entries.
  useEffect(() => {
    if (opened) return;
    setOverrides({});
    setAddedAdviserIds([]);
    setAddedClients([]);
    setAddedConnectionIds([]);
    setCustomAttendees([]);
    setCustomSeq(0);
    setValidationTriggered(false);
  }, [opened]);

  const meetingIds = new Set(meetingAttendees.map(attendee => attendee.id));

  const addedAdviserAttendees: Attendee[] = advisors
    .filter(advisor => addedAdviserIds.includes(advisor.id))
    .map(adviserToAttendee)
    .filter(attendee => !meetingIds.has(attendee.id));

  const clientById = new Map(clients.map(client => [client.id, client]));
  const addedClientAttendees: Attendee[] = addedClients
    .map(({ clientId, group }) => {
      const client = clientById.get(clientId);
      return client ? clientToAttendee(client, group) : null;
    })
    .filter((attendee): attendee is Attendee => !!attendee && !meetingIds.has(attendee.id));

  const connectionById = new Map(connections.map(connection => [connection.id, connection]));
  const addedConnectionAttendees: Attendee[] = addedConnectionIds
    .map(id => connectionById.get(id))
    .filter((connection): connection is ProfessionalConnection => !!connection)
    .map(connectionToAttendee);

  const customAsAttendees: Attendee[] = customAttendees.map(custom => ({
    id: custom.id,
    name: custom.name,
    role: custom.role,
    group: custom.group,
    participantId: null,
    defaultChecked: false,
    addBody: { participantType: 'EXTERNAL', name: custom.name, description: custom.role },
    isExternalGuest: true,
  }));

  const attendees = [
    ...meetingAttendees,
    ...addedAdviserAttendees,
    ...addedClientAttendees,
    ...addedConnectionAttendees,
    ...customAsAttendees,
  ];

  const isChecked = (attendee: Attendee) => overrides[attendee.id] ?? attendee.defaultChecked;

  const toggle = (attendee: Attendee) =>
    setOverrides(previous => ({
      ...previous,
      [attendee.id]: !(previous[attendee.id] ?? attendee.defaultChecked),
    }));

  const shownIds = new Set(attendees.map(attendee => attendee.id));
  const shownKeyContactNames = new Set(
    attendees
      .filter(attendee => attendee.group === 'keyContact')
      .map(attendee => attendee.name.trim().toLowerCase()),
  );

  const adviserResults = (search: string) =>
    advisors
      .filter(isSelectableAdvisor)
      .filter(advisor => !shownIds.has(`user:${advisor.id}`))
      .filter(advisor => matchesSearch(`${fullName(advisor)} ${advisor.emailAddress}`, search));

  const clientResults = (search: string) =>
    clients
      .filter(client => !shownIds.has(`client:${client.id}`))
      .filter(client => matchesSearch(`${fullName(client)} ${client.emailAddress ?? ''}`, search));

  const connectionResults = (search: string) =>
    connections
      .filter(connection => !shownIds.has(`conn:${connection.id}`))
      .filter(connection => !shownKeyContactNames.has(connection.name.trim().toLowerCase()))
      .filter(connection => matchesSearch(`${connection.name} ${connection.email}`, search));

  function selectAdviser(advisorId: string) {
    setAddedAdviserIds(previous => [...previous, advisorId]);
    setOverrides(previous => ({ ...previous, [`user:${advisorId}`]: true }));
  }

  function selectClient(clientId: string, group: AttendeeGroup) {
    setAddedClients(previous => [...previous, { clientId, group }]);
    setOverrides(previous => ({ ...previous, [`client:${clientId}`]: true }));
  }

  function selectConnection(connectionId: string) {
    setAddedConnectionIds(previous => [...previous, connectionId]);
    setOverrides(previous => ({ ...previous, [`conn:${connectionId}`]: true }));
  }

  function addCustomAttendee(group: AttendeeGroup, typed: string) {
    const name = typed.trim();
    if (!name) return;
    const id = `custom:${customSeq}`;
    setCustomSeq(previous => previous + 1);
    setCustomAttendees(previous => [...previous, { id, name, role: '', group }]);
    setOverrides(previous => ({ ...previous, [id]: true }));
  }

  function setCustomRole(id: string, role: string) {
    setCustomAttendees(previous =>
      previous.map(custom => (custom.id === id ? { ...custom, role } : custom)),
    );
  }

  const hasCheckedInGroup = (group: AttendeeGroup) =>
    attendees.some(attendee => attendee.group === group && isChecked(attendee));

  async function handleAdd() {
    const missingRequiredGroup =
      meetingHasClient &&
      (Object.keys(REQUIRED_GROUP_MESSAGES) as AttendeeGroup[]).some(
        group => !hasCheckedInGroup(group),
      );
    if (missingRequiredGroup) {
      setValidationTriggered(true);
      return;
    }

    const operations: Promise<unknown>[] = [];
    for (const attendee of attendees) {
      const checked = isChecked(attendee);
      if (checked && !attendee.defaultChecked) {
        operations.push(
          addParticipant.mutateAsync({ eventId: meeting.id, body: attendee.addBody }),
        );
      } else if (!checked && attendee.defaultChecked && attendee.participantId) {
        operations.push(
          removeParticipant.mutateAsync({
            eventId: meeting.id,
            participantId: attendee.participantId,
          }),
        );
      }
    }

    if (operations.length === 0) {
      onClose();
      return;
    }

    setIsSaving(true);
    try {
      await Promise.all(operations);
    } catch (error) {
      setIsSaving(false);
      notifyAndLogError(
        'Failed to update attendees',
        'Could not update the meeting attendees. Please try again.',
        error,
      );
      return;
    }
    setIsSaving(false);
    onClose();
  }

  const toRow = (attendee: Attendee): SelectPersonRow => ({
    id: attendee.id,
    name: attendee.name,
    role: attendee.role,
    checked: isChecked(attendee),
    onToggle: () => toggle(attendee),
    ariaLabel: `${attendee.name} attending`,
    secondaryLabel: attendee.isExternalGuest ? <ExternalGuestLabel /> : undefined,
    renderCustom: attendee.id.startsWith('custom:')
      ? () => (
          <Flex align="center" gap="sm" py="xxxs">
            <Box style={{ width: 160 }}>
              <Text size="sm" fw={FONT_WEIGHTS.medium} c="gray.10">
                {attendee.name}
              </Text>
              {attendee.isExternalGuest && <ExternalGuestLabel />}
            </Box>
            <TextInput
              size="md"
              placeholder="Add role"
              value={attendee.role}
              onChange={event => setCustomRole(attendee.id, event.currentTarget.value)}
              style={{ flex: 1 }}
            />
            <Checkbox
              checked={isChecked(attendee)}
              onChange={() => toggle(attendee)}
              aria-label={attendee.name}
            />
          </Flex>
        )
      : undefined,
  });

  const resultsForGroup = (group: AttendeeGroup, search: string): SelectResultRow[] => {
    if (group === 'adviser') {
      return adviserResults(search).map(advisor => ({
        id: advisor.id,
        name: fullName(advisor),
        email: advisor.emailAddress,
        onAdd: () => selectAdviser(advisor.id),
      }));
    }
    if (group === 'guest') {
      return clientResults(search).map(client => ({
        id: client.id,
        name: fullName(client),
        email: client.emailAddress ?? '',
        onAdd: () => selectClient(client.id, 'guest'),
      }));
    }
    return connectionResults(search).map(connection => ({
      id: connection.id,
      name: connection.name,
      email: connection.email,
      onAdd: () => selectConnection(connection.id),
    }));
  };

  const sections: SelectPeopleSection[] = SECTIONS.map(section => ({
    key: section.group,
    title: section.title,
    addLabel: section.addLabel,
    searchPlaceholder: section.searchPlaceholder,
    noneLabel: section.noneLabel,
    addAsWord: section.addAsWord,
    allowFreeText: section.group !== 'adviser',
    requiredMessage:
      meetingHasClient &&
      REQUIRED_GROUP_MESSAGES[section.group] &&
      !hasCheckedInGroup(section.group)
        ? REQUIRED_GROUP_MESSAGES[section.group]
        : undefined,
    rows: attendees.filter(attendee => attendee.group === section.group).map(toRow),
    getResults: (search: string) => resultsForGroup(section.group, search),
    onAddFreeText: (typed: string) => addCustomAttendee(section.group, typed),
  }));

  return (
    <SelectPeopleModal
      opened={opened}
      onClose={onClose}
      title={modalTitle}
      confirmLabel="Add"
      searchConfirmLabel="Click done before saving attendees"
      isSaving={isSaving}
      sections={sections}
      validationTriggered={validationTriggered}
      onConfirm={handleAdd}
    />
  );
}
